import { cloneDeep } from "lodash";
import { migrateTraversal } from "../helpers/migrateTraversal";
import { WebsiteBuilderMigrationError } from "../helpers/migrationError";
import { IWebsiteBuilderMigrationNode, IWebsiteBuilderSchemaVersionMigrationNode } from "../helpers/migrationNode";

interface IMigrateElementV6Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: string;
    settings: Record<string, unknown>;
  }
}

export interface IMigrateButtonV6Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "button";
    settings: {
      roundedCorners: boolean;
    }
  }
}

export interface IMigrateButtonV7Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "button";
    settings: {
      borderRadius: number;
    }
  }
}

function isMigrateElementV6Input(node: any): node is IMigrateElementV6Input {
  const partialNode: Partial<IMigrateElementV6Input> = node;

  return (
    partialNode?.type === "element" &&
    partialNode.element?.type != null &&
    "settings" in partialNode.element
  );
}

function isMigrateButtonV6Input(node: IMigrateElementV6Input): node is IMigrateButtonV6Input {
  return (
    node.element.type === "button" &&
    node.element.settings != null &&
    "roundedCorners" in node.element.settings &&
    (node.element.settings.roundedCorners == null || typeof node.element.settings.roundedCorners === "boolean")
  );
}

export function migrateSchemaV6ToV7(node: IWebsiteBuilderSchemaVersionMigrationNode): IWebsiteBuilderSchemaVersionMigrationNode {
  if (node.schemaVersion !== 6) {
    throw new WebsiteBuilderMigrationError({
      type: "cannot-migrate-from-schema-version",
      schemaVersion: node.schemaVersion,
      nextSchemaVersion: 7,
    });
  }

  function migrateNode(prev: IWebsiteBuilderMigrationNode): IWebsiteBuilderMigrationNode {
    const throwElementMissingFieldError = () => {
      throw new WebsiteBuilderMigrationError({
        type: "element-missing-field",
        schemaVersion: 6,
        nextSchemaVersion: 7,
      });
    };

    if (prev?.type === "element") {
      if (!isMigrateElementV6Input(prev)) {
        throwElementMissingFieldError();
      } else if (prev.element.type === "button") {
        if (!isMigrateButtonV6Input(prev)) {
          throwElementMissingFieldError();
        } else {
          const buttonBaseV7Result: IMigrateButtonV6Input = cloneDeep(prev);

          const borderRadiusV7Result = buttonBaseV7Result.element.settings.roundedCorners ? 10 : 0;

          delete buttonBaseV7Result.element.settings.roundedCorners;

          const buttonV7Result: IMigrateButtonV7Result = {
            ...buttonBaseV7Result,
            element: {
              ...buttonBaseV7Result.element,
              settings: {
                ...buttonBaseV7Result.element.settings,
                borderRadius: borderRadiusV7Result,
              },
            },
          };

          return buttonV7Result;
        }
      } else {
        return prev;
      }
    } else {
      return prev;
    }
  }

  return {
    ...migrateTraversal(node, migrateNode),
    schemaVersion: 7,
  };
}
